import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
    CheckIcon,
    CopyIcon,
    ExternalLinkIcon,
    GlobeIcon,
    LockKeyholeIcon,
    NetworkIcon,
    PlusIcon,
    RefreshCwIcon,
    RouteIcon,
    ShieldCheckIcon,
    Trash2Icon,
    XIcon,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import {
    Dialog,
    DialogBody,
    DialogDescription,
    DialogTitle,
    ScrollDialogContent,
    ScrollDialogFooter,
    ScrollDialogHeader,
} from '@/components/ui/dialog-scroll';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import useDialog from '@/hooks/useDialog';
import server from '@/services/server';
import { useClub } from '../ClubContext';
import useClubErrors from '../hooks/useClubErrors';
import useClubPortalContainer from '../hooks/useClubPortalContainer';
import { useClubConfirm } from '../providers/ClubConfirmProvider';
import ClubSettingsCardHeader from './ClubSettingsCardHeader';

const DEFAULT_CNAME_TARGET = 'clubes.seuclubedebeneficios.com.br';

const STATUS_COPY = {
    processing: { label: 'Verificando DNS', tone: 'secondary' },
    provisioning_hostname: { label: 'Emitindo certificado', tone: 'secondary' },
    provisioning_route: { label: 'Conectando ao clube', tone: 'secondary' },
    verifying: { label: 'Finalizando ativação', tone: 'secondary' },
    active: { label: 'Domínio ativo', tone: 'default' },
    removing: { label: 'Removendo domínio', tone: 'outline' },
    failed_cname: { label: 'CNAME ausente ou incorreto', tone: 'destructive' },
    failed: { label: 'Não foi possível concluir', tone: 'destructive' },
};

const IN_FLIGHT = new Set([
    'processing',
    'provisioning_hostname',
    'provisioning_route',
    'verifying',
    'removing',
]);

/** Etapas do provisionamento — alinhadas à fila club-domain. */
const PIPELINE_STEPS = [
    { id: 'cname', label: 'CNAME', short: 'Verificação', Icon: NetworkIcon },
    { id: 'ssl', label: 'SSL', short: 'Certificado', Icon: LockKeyholeIcon },
    { id: 'route', label: 'Rota', short: 'Conexão', Icon: RouteIcon },
    { id: 'ready', label: 'Ativo', short: 'Pronto', Icon: ShieldCheckIcon },
];

const STATUS_STEP_INDEX = {
    processing: 0,
    provisioning_hostname: 1,
    provisioning_route: 2,
    verifying: 3,
    active: 4,
};

function resolveFailedStepIndex(status, errorMessage) {
    if (status === 'failed_cname') return 0;
    if (status !== 'failed') return -1;

    const msg = String(errorMessage || '').toLowerCase();
    if (/cname|dns/.test(msg)) return 0;
    if (/ssl|certificado|hostname/.test(msg)) return 1;
    if (/worker|route|rota|conect/.test(msg)) return 2;
    return 3;
}

/**
 * @returns {'done'|'current'|'pending'|'failed'|'idle'}[]
 */
function getPipelineStates(status, errorMessage) {
    if (!status || status === 'removing') {
        return PIPELINE_STEPS.map(() => 'idle');
    }

    const failedAt = resolveFailedStepIndex(status, errorMessage);
    if (failedAt >= 0) {
        return PIPELINE_STEPS.map((_, index) => {
            if (index < failedAt) return 'done';
            if (index === failedAt) return 'failed';
            return 'pending';
        });
    }

    if (status === 'active') {
        return PIPELINE_STEPS.map(() => 'done');
    }

    const currentIndex = STATUS_STEP_INDEX[status];
    if (currentIndex == null) {
        return PIPELINE_STEPS.map(() => 'idle');
    }

    return PIPELINE_STEPS.map((_, index) => {
        if (index < currentIndex) return 'done';
        if (index === currentIndex) return 'current';
        return 'pending';
    });
}

const PIPELINE_ICON_CLASS = {
    done: 'border-emerald-500/40 bg-emerald-500 text-white shadow-sm shadow-emerald-500/20',
    current: 'border-sky-500/50 bg-sky-500 text-white shadow-sm shadow-sky-500/25 ring-4 ring-sky-500/15',
    failed: 'border-destructive/40 bg-destructive text-white shadow-sm shadow-destructive/20',
    pending: 'border-border bg-muted text-muted-foreground',
    idle: 'border-border bg-muted/60 text-muted-foreground/70',
};

const PIPELINE_LINE_CLASS = {
    done: 'bg-emerald-500',
    current: 'bg-sky-500/40',
    failed: 'bg-destructive/40',
    pending: 'bg-border',
    idle: 'bg-border',
};

function DomainPipeline({ status, errorMessage }) {
    const states = getPipelineStates(status, errorMessage);

    if (!status || status === 'removing') return null;

    return (
        <div className="rounded-lg border border-border bg-muted/20 px-3 py-4 sm:px-4">
            <p className="mb-3 text-xs font-medium text-muted-foreground">Progresso da verificação</p>
            <ol className="flex items-start justify-between gap-1">
                {PIPELINE_STEPS.map((step, index) => {
                    const state = states[index];
                    const StepIcon = step.Icon;
                    const isLast = index === PIPELINE_STEPS.length - 1;

                    return (
                        <li key={step.id} className="relative flex min-w-0 flex-1 flex-col items-center text-center">
                            {!isLast ? (
                                <span
                                    aria-hidden
                                    className={cn(
                                        'absolute top-4 left-[calc(50%+1.1rem)] right-[calc(-50%+1.1rem)] h-0.5',
                                        PIPELINE_LINE_CLASS[state === 'done' ? 'done' : 'pending'],
                                    )}
                                />
                            ) : null}

                            <span
                                className={cn(
                                    'relative z-10 flex size-8 items-center justify-center rounded-full border',
                                    PIPELINE_ICON_CLASS[state],
                                )}
                                aria-current={state === 'current' ? 'step' : undefined}
                            >
                                {state === 'done' ? (
                                    <CheckIcon className="size-4" strokeWidth={2.5} />
                                ) : state === 'failed' ? (
                                    <XIcon className="size-4" strokeWidth={2.5} />
                                ) : state === 'current' ? (
                                    <Spinner className="size-4 text-white" />
                                ) : (
                                    <StepIcon className="size-3.5" />
                                )}
                            </span>

                            <span
                                className={cn(
                                    'mt-2 text-[11px] leading-tight font-medium sm:text-xs',
                                    state === 'done' && 'text-emerald-700 dark:text-emerald-400',
                                    state === 'current' && 'text-sky-700 dark:text-sky-400',
                                    state === 'failed' && 'text-destructive',
                                    (state === 'pending' || state === 'idle') && 'text-muted-foreground',
                                )}
                            >
                                {step.label}
                            </span>
                            <span className="mt-0.5 hidden text-[10px] text-muted-foreground sm:block">
                                {step.short}
                            </span>
                        </li>
                    );
                })}
            </ol>
        </div>
    );
}

const domainSchema = z.object({
    domain: z
        .string()
        .min(1, 'Campo obrigatório')
        .refine(
            value => !value.startsWith('http://') && !value.startsWith('https://'),
            'Não inclua http:// ou https://',
        ),
});

function formatUpdatedAt(value) {
    if (!value) return null;
    try {
        return new Date(value).toLocaleString('pt-BR');
    } catch {
        return null;
    }
}

function useDomainStatusPoll({ enabled, onData }) {
    const timerRef = useRef(null);
    const onDataRef = useRef(onData);
    onDataRef.current = onData;

    useEffect(() => {
        if (!enabled) {
            if (timerRef.current) clearInterval(timerRef.current);
            return undefined;
        }

        const tick = async () => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
            try {
                const { data } = await server.get('club/settings/domains/verify');
                onDataRef.current?.(data?.data || data);
            } catch {
                // falha temporária não reinicia o fluxo
            }
        };

        tick();
        timerRef.current = setInterval(tick, 15_000);
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [enabled]);
}

function DialogCustomDomain({ data, mutate, open, onOpenChange }) {
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const confirm = useClubConfirm();
    const portalContainer = useClubPortalContainer();
    const [statusSnapshot, setStatusSnapshot] = useState(null);

    const form = useForm({
        resolver: zodResolver(domainSchema),
        defaultValues: { domain: '' },
    });

    useEffect(() => {
        if (!open) return;
        setStatusSnapshot(null);
        form.reset({ domain: data?.domain || '' });
    }, [open, data?.domain, form]);

    const merged = useMemo(() => {
        const base = {
            domain: data?.domain || null,
            domainStatus: data?.domainStatus || null,
            domainErrorMessage: data?.domainErrorMessage || null,
            domainStatusUpdatedAt: data?.domainStatusUpdatedAt || null,
            cnameTarget: data?.cnameTarget || DEFAULT_CNAME_TARGET,
        };
        return { ...base, ...(statusSnapshot || {}) };
    }, [data, statusSnapshot]);

    const status = merged.domainStatus;
    const hasDomain = Boolean(merged.domain);
    const isBusy = IN_FLIGHT.has(status);
    const canRetry = status === 'failed' || status === 'failed_cname';
    const statusMeta = STATUS_COPY[status] || null;

    useDomainStatusPoll({
        enabled: open && isBusy,
        onData: payload => {
            setStatusSnapshot(payload);
            if (payload?.domainStatus && !IN_FLIGHT.has(payload.domainStatus)) {
                mutate?.();
            }
        },
    });

    const copyText = async (label, value) => {
        try {
            await navigator.clipboard.writeText(value);
            enqueueSnackbar(`${label} copiado.`);
        } catch {
            enqueueSnackbar('Não foi possível copiar.', { variant: 'error' });
        }
    };

    const onAdd = async values => {
        try {
            const hostname = String(values.domain || '')
                .trim()
                .toLowerCase()
                .replace(/^https?:\/\//, '')
                .replace(/\.$/, '');

            await confirm({
                title: 'Adicionar domínio personalizado',
                description: `Você está adicionando o domínio ${hostname}. Configure o CNAME antes ou em paralelo.`,
                confirmationText: 'Adicionar',
            });

            await server.put('club/settings/domains', { domain: hostname });
            enqueueSnackbar('Domínio aceito. Acompanhe a verificação.');
            await mutate();
            setStatusSnapshot({
                domain: hostname,
                domainStatus: 'processing',
            });
        } catch (error) {
            if (error?.message === 'cancelled') return;
            setErrors(error);
        }
    };

    const onRetry = async () => {
        if (!merged.domain) return;
        try {
            await server.put('club/settings/domains', { domain: merged.domain });
            enqueueSnackbar('Nova tentativa iniciada.');
            await mutate();
            setStatusSnapshot(prev => ({
                ...(prev || {}),
                domainStatus: 'processing',
                domainErrorMessage: null,
            }));
        } catch (error) {
            setErrors(error);
        }
    };

    const onRemove = async () => {
        try {
            await confirm({
                title: 'Remover domínio',
                description: 'O domínio deixará de atender o site do clube após a remoção.',
                confirmationText: 'Remover',
                confirmationButtonProps: { variant: 'destructive' },
            });

            const { data: response } = await server.delete('club/settings/domains');
            const payload = response?.data || response;
            await mutate();
            if (!payload?.domain && !payload?.domainStatus) {
                enqueueSnackbar('Domínio removido.');
                setStatusSnapshot(null);
                onOpenChange(false);
                return;
            }
            enqueueSnackbar('Remoção iniciada. Aguarde a confirmação.');
            setStatusSnapshot(prev => ({
                ...(prev || {}),
                domainStatus: 'removing',
            }));
        } catch (error) {
            if (error?.message === 'cancelled') return;
            setErrors(error);
        }
    };

    const { errors, isSubmitting } = form.formState;
    const cnameTarget = merged.cnameTarget || DEFAULT_CNAME_TARGET;
    const hostHint = merged.domain || 'beneficios.suaempresa.com.br';
    const updatedLabel = formatUpdatedAt(merged.domainStatusUpdatedAt);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <ScrollDialogContent container={portalContainer} className="sm:max-w-xl">
                <ScrollDialogHeader>
                    <DialogTitle>Domínio personalizado</DialogTitle>
                    <DialogDescription>
                        Aponte um CNAME para o clube. O SSL e a conexão são provisionados automaticamente.
                    </DialogDescription>
                </ScrollDialogHeader>

                <form onSubmit={form.handleSubmit(onAdd)} className="contents">
                    <DialogBody>
                        <div className="flex flex-col gap-4">
                            <div className="space-y-2 text-sm">
                                <p className="font-medium">Configuração DNS</p>
                                <ol className="list-decimal space-y-1 pl-5 text-muted-foreground">
                                    <li>No provedor de DNS, crie um registro <strong>CNAME</strong>.</li>
                                    <li>
                                        Nome/Host: o subdomínio escolhido (ex.: <code>{hostHint}</code>).
                                    </li>
                                    <li>Destino: o valor abaixo (sem A/AAAA concorrente no mesmo host).</li>
                                    <li>Proxy Cloudflare (laranja) é permitido; o SSL é emitido automaticamente.</li>
                                </ol>
                            </div>

                            <div className="flex items-center justify-between gap-2 rounded-lg border border-border p-3">
                                <div className="min-w-0">
                                    <p className="text-xs text-muted-foreground">Destino (CNAME)</p>
                                    <p className="truncate font-mono text-sm">{cnameTarget}</p>
                                </div>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => copyText('Destino', cnameTarget)}
                                >
                                    <CopyIcon className="size-3.5" />
                                    Copiar
                                </Button>
                            </div>

                            <Alert>
                                <AlertDescription>
                                    A propagação do DNS pode levar até 48 horas. Enquanto isso, o status permanece em
                                    verificação — isso não é um erro.
                                </AlertDescription>
                            </Alert>

                            {!hasDomain || canRetry ? (
                                <FieldGroup>
                                    <Field data-invalid={Boolean(errors.domain)}>
                                        <FieldLabel htmlFor="custom-domain">Seu domínio</FieldLabel>
                                        <Controller
                                            name="domain"
                                            control={form.control}
                                            render={({ field }) => (
                                                <Input
                                                    id="custom-domain"
                                                    placeholder="Exemplo: beneficios.suaempresa.com.br"
                                                    disabled={isBusy || (hasDomain && !canRetry)}
                                                    aria-invalid={Boolean(errors.domain)}
                                                    {...field}
                                                    value={field.value ?? ''}
                                                />
                                            )}
                                        />
                                        <FieldError>{errors.domain?.message}</FieldError>
                                    </Field>
                                </FieldGroup>
                            ) : (
                                <div>
                                    <p className="text-xs text-muted-foreground">Domínio configurado</p>
                                    <p className="font-medium">{merged.domain}</p>
                                </div>
                            )}

                            {statusMeta || status ? (
                                <div className="space-y-3 border-t border-border pt-4">
                                    {statusMeta ? (
                                        <div className="flex flex-wrap items-center gap-2">
                                            <Badge variant={statusMeta.tone}>{statusMeta.label}</Badge>
                                            {updatedLabel ? (
                                                <span className="text-xs text-muted-foreground">
                                                    Atualizado em {updatedLabel}
                                                </span>
                                            ) : null}
                                        </div>
                                    ) : null}

                                    <DomainPipeline
                                        status={status}
                                        errorMessage={merged.domainErrorMessage}
                                    />

                                    {(status === 'failed' || status === 'failed_cname') &&
                                    merged.domainErrorMessage ? (
                                        <Alert variant="destructive">
                                            <AlertTitle>Erro</AlertTitle>
                                            <AlertDescription className="whitespace-pre-wrap text-sm">
                                                {merged.domainErrorMessage}
                                            </AlertDescription>
                                        </Alert>
                                    ) : null}

                                    {status === 'active' ? (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="w-fit"
                                            render={
                                                <a href={`https://${merged.domain}`} target="_blank" rel="noreferrer" />
                                            }
                                        >
                                            <ExternalLinkIcon className="size-4" />
                                            Abrir site
                                        </Button>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    </DialogBody>

                    <ScrollDialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Fechar
                        </Button>
                        {hasDomain ? (
                            <>
                                {canRetry ? (
                                    <Button type="button" variant="secondary" disabled={isSubmitting} onClick={onRetry}>
                                        {isSubmitting ? <Spinner className="size-4" /> : <RefreshCwIcon className="size-4" />}
                                        Tentar novamente
                                    </Button>
                                ) : null}
                                <Button
                                    type="button"
                                    variant="destructive"
                                    disabled={isSubmitting || status === 'removing'}
                                    onClick={onRemove}
                                >
                                    {isSubmitting || status === 'removing' ? (
                                        <Spinner className="size-4" />
                                    ) : (
                                        <Trash2Icon className="size-4" />
                                    )}
                                    Remover
                                </Button>
                            </>
                        ) : (
                            <Button type="submit" disabled={isSubmitting || isBusy}>
                                {isSubmitting ? <Spinner className="size-4" /> : <PlusIcon className="size-4" />}
                                Adicionar
                            </Button>
                        )}
                    </ScrollDialogFooter>
                </form>
            </ScrollDialogContent>
        </Dialog>
    );
}

export default function DominioClub({ data, mutate }) {
    const { open, handleClose } = useDialog('#custom-domain');
    const { health } = useClub();
    const attention = health?.checks?.domain?.ok === false;
    const statusMeta = STATUS_COPY[data?.domainStatus];

    return (
        <>
            <Card>
                <CardHeader>
                    <ClubSettingsCardHeader
                        icon={GlobeIcon}
                        title="Domínio personalizado"
                        description="URL própria para o portal do clube."
                        attention={attention}
                    />
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                    <div>
                        <span className="text-muted-foreground">Seu domínio</span>
                        <p className="font-medium">{data?.domain || 'Nenhum domínio configurado'}</p>
                    </div>
                    {statusMeta ? (
                        <div className="flex items-center gap-2">
                            <Badge variant={statusMeta.tone}>{statusMeta.label}</Badge>
                            {data?.domainStatus === 'active' ? (
                                <CheckIcon className="size-4 text-emerald-600" aria-hidden />
                            ) : null}
                        </div>
                    ) : null}
                </CardContent>
                <CardFooter>
                    <Button variant="outline" size="sm" render={<Link to="#custom-domain" />}>
                        <GlobeIcon className="size-4" />
                        {data?.domain ? 'Ver detalhes' : 'Adicionar domínio'}
                    </Button>
                </CardFooter>
            </Card>

            <DialogCustomDomain
                data={data}
                mutate={mutate}
                open={open}
                onOpenChange={isOpen => !isOpen && handleClose()}
            />
        </>
    );
}
